/**
 * Applications Copilot — streaming client for POST /api/copilot/chat.
 *
 * axios cannot consume a ReadableStream, so this hook uses `fetch` with
 * `credentials: 'include'` (the global apiAuthInterceptor patches
 * window.fetch and adds the X-*-Token headers), reads
 * `response.body.getReader()`, decodes with TextDecoder, splits the SSE
 * stream on blank lines and dispatches each `data: <json>` event by `type`
 * into reducer state describing the in-flight assistant message.
 *
 * Event types (COPILOT_API_CONTRACT.md): meta, status, delta, block,
 * citations, speech, done, error.
 */
import { useCallback, useEffect, useReducer, useRef } from 'react';
import { getApiUrl } from '../../../../config/api';
import { cancelStream } from './copilotApi';

export const CHAT_ENDPOINT = '/api/copilot/chat';

// ---------------------------------------------------------------------------
// SSE parsing (pure, exported for tests)
// ---------------------------------------------------------------------------

/**
 * Incremental SSE parser. Feed it decoded text chunks in any split; it
 * yields complete events (parsed JSON of the `data:` payload) and keeps the
 * unterminated tail in its buffer until the next feed.
 */
export function createSSEParser() {
  let buffer = '';

  function parseEventBlock(raw) {
    const dataLines = [];
    raw.split(/\r?\n/).forEach((line) => {
      if (!line || line.startsWith(':')) return; // comment / keep-alive
      if (line.startsWith('data:')) dataLines.push(line.slice(5).replace(/^ /, ''));
    });
    if (!dataLines.length) return null;
    const payload = dataLines.join('\n').trim();
    if (!payload) return null;
    try {
      return JSON.parse(payload);
    } catch {
      return { type: 'error', message: 'Received a malformed event from the server.', code: 'bad_event', retryable: true };
    }
  }

  return {
    /** @returns {object[]} complete events found in the buffer after appending `text` */
    feed(text) {
      buffer += text || '';
      const events = [];
      let match = /\r?\n\r?\n/.exec(buffer);
      while (match) {
        const raw = buffer.slice(0, match.index);
        buffer = buffer.slice(match.index + match[0].length);
        const evt = parseEventBlock(raw);
        if (evt) events.push(evt);
        match = /\r?\n\r?\n/.exec(buffer);
      }
      return events;
    },
    /** Flush whatever is left (stream closed without a trailing blank line). */
    flush() {
      const rest = buffer;
      buffer = '';
      if (!rest.trim()) return [];
      const evt = parseEventBlock(rest);
      return evt ? [evt] : [];
    },
  };
}

function makeDecoder() {
  if (typeof TextDecoder !== 'undefined') {
    const decoder = new TextDecoder('utf-8');
    return (value, stream) => decoder.decode(value, { stream });
  }
  return (value) => {
    if (typeof value === 'string') return value;
    try {
      return Buffer.from(value).toString('utf8');
    } catch {
      return String.fromCharCode.apply(null, value);
    }
  };
}

/**
 * Read a fetch Response body as SSE and call `onEvent` for each event.
 * Resolves when the body ends. Rejects on reader errors / abort.
 */
export async function readSSEStream(response, onEvent) {
  const reader = response.body.getReader();
  const parser = createSSEParser();
  const decode = makeDecoder();
  // eslint-disable-next-line no-constant-condition
  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    const text = typeof value === 'string' ? value : decode(value, true);
    parser.feed(text).forEach(onEvent);
  }
  parser.flush().forEach(onEvent);
}

// ---------------------------------------------------------------------------
// Reducer
// ---------------------------------------------------------------------------

export const INITIAL_STREAM_STATE = Object.freeze({
  active: false,
  isStreaming: false,
  id: null,
  streamId: null,
  conversationId: null,
  content: '',
  blocks: [],
  citations: [],
  speech: null,
  statusLines: [],
  toolTrace: [],
  usage: null,
  latencyMs: null,
  error: null,
  cancelled: false,
  voice: false,
});

export function streamReducer(state, action) {
  switch (action.type) {
    case 'start':
      return {
        ...INITIAL_STREAM_STATE,
        active: true,
        isStreaming: true,
        conversationId: action.conversationId ?? null,
        voice: Boolean(action.voice),
        blocks: [],
        citations: [],
        statusLines: [],
        toolTrace: [],
      };
    case 'meta':
      return {
        ...state,
        id: action.event.message_id ?? state.id,
        streamId: action.event.stream_id ?? state.streamId,
        conversationId: action.event.conversation_id ?? state.conversationId,
      };
    case 'status': {
      const text = String(action.event.text || '').trim();
      if (!text) return state;
      return { ...state, statusLines: [...state.statusLines, text] };
    }
    case 'delta':
      return { ...state, content: state.content + String(action.event.text ?? '') };
    case 'block':
      return action.event.block && typeof action.event.block === 'object'
        ? { ...state, blocks: [...state.blocks, action.event.block] }
        : state;
    case 'citations':
      return { ...state, citations: Array.isArray(action.event.items) ? action.event.items : state.citations };
    case 'speech':
      return { ...state, speech: typeof action.event.text === 'string' ? action.event.text : state.speech };
    case 'done':
      return {
        ...state,
        isStreaming: false,
        id: action.event.message_id ?? state.id,
        usage: action.event.usage || null,
        latencyMs: action.event.latency_ms ?? null,
        toolTrace: Array.isArray(action.event.tool_trace) ? action.event.tool_trace : state.toolTrace,
      };
    case 'error':
      return {
        ...state,
        isStreaming: false,
        error: {
          message: action.error?.message || 'The assistant could not answer.',
          code: action.error?.code || 'error',
          retryable: action.error?.retryable !== false,
          status: action.error?.status ?? null,
          retryAfter: action.error?.retryAfter ?? null,
          upgradeRequired: Boolean(action.error?.upgradeRequired),
        },
      };
    case 'cancelled':
      return { ...state, isStreaming: false, cancelled: true };
    case 'reset':
      return INITIAL_STREAM_STATE;
    default:
      return state;
  }
}

const EVENT_TYPES = new Set(['meta', 'status', 'delta', 'block', 'citations', 'speech', 'done', 'error']);

async function parseErrorResponse(response) {
  let data = {};
  try {
    data = await response.json();
  } catch {
    data = {};
  }
  const status = response.status;
  const defaults = {
    400: 'That message could not be sent.',
    401: 'Your session has expired. Please sign in again.',
    403: 'You do not have access to the copilot for this job.',
    409: 'Another answer is still streaming. Wait for it to finish.',
    429: 'Too many questions in a short time. Please wait a moment.',
    503: 'The copilot is turned off right now.',
  };
  return {
    status,
    code: data.code || data.error || `http_${status}`,
    message: data.message || (typeof data.error === 'string' && data.error !== 'copilot_disabled' ? data.error : null) || defaults[status] || `Request failed (${status}).`,
    retryable: status === 429 || status === 409 || status >= 500,
    retryAfter: Number(data.retry_after) || null,
    upgradeRequired: Boolean(data.upgrade_required),
  };
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/**
 * @param {object} options
 * @param {(event: object, state: object) => void} [options.onEvent]  every SSE event
 * @param {(message: object) => void} [options.onFinish]  called once when the stream ends
 *   (done, error, cancel or network failure) with the assembled message state.
 */
export function useCopilotStream({ onEvent, onFinish } = {}) {
  const [state, dispatch] = useReducer(streamReducer, INITIAL_STREAM_STATE);
  const stateRef = useRef(state);
  stateRef.current = state;
  const controllerRef = useRef(null);
  const lastRequestRef = useRef(null);
  const onEventRef = useRef(onEvent);
  const onFinishRef = useRef(onFinish);
  onEventRef.current = onEvent;
  onFinishRef.current = onFinish;
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (controllerRef.current) {
        try {
          controllerRef.current.abort();
        } catch {
          // ignore
        }
      }
    };
  }, []);

  const finish = useCallback((finalPatch) => {
    const snapshot = { ...stateRef.current, ...finalPatch, isStreaming: false };
    controllerRef.current = null;
    if (onFinishRef.current) onFinishRef.current(snapshot);
  }, []);

  /**
   * Send a message. Resolves when the stream has fully ended.
   * @param {{ scope?:{type:'company'}|{type:'job',job_id:number}, jobId?:number, message:string,
   *           conversationId?:number, voice?:boolean, uiContext?:object }} req
   *   `scope` is preferred (G1); a bare `jobId` still means job scope.
   */
  const send = useCallback(
    async (req) => {
      if (!req || !String(req.message || '').trim()) return null;
      const scope = req.scope && typeof req.scope === 'object' ? req.scope : null;
      const jobId = scope ? (scope.type === 'job' ? scope.job_id : null) : req.jobId ?? null;
      if (!scope && !jobId) return null;
      if (controllerRef.current) return null; // one stream at a time

      const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
      controllerRef.current = controller;
      lastRequestRef.current = req;
      dispatch({ type: 'start', conversationId: req.conversationId ?? null, voice: req.voice });

      const body = {
        message: String(req.message).slice(0, 2000),
        voice: Boolean(req.voice),
      };
      if (scope) body.scope = scope.type === 'job' ? { type: 'job', job_id: scope.job_id } : { type: 'company' };
      if (jobId !== null && jobId !== undefined) body.job_id = jobId;
      if (req.conversationId) body.conversation_id = req.conversationId;
      if (req.uiContext) body.ui_context = req.uiContext;

      let localState = { ...INITIAL_STREAM_STATE, active: true, isStreaming: true };
      const apply = (action) => {
        localState = streamReducer(localState, action);
        if (mountedRef.current) dispatch(action);
      };

      let streamIdSeen = null;
      let terminal = false;
      try {
        const response = await fetch(getApiUrl(CHAT_ENDPOINT), {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json', Accept: 'text/event-stream' },
          body: JSON.stringify(body),
          signal: controller ? controller.signal : undefined,
        });

        if (!response.ok) {
          const error = await parseErrorResponse(response);
          apply({ type: 'error', error });
          finish(localState);
          return localState;
        }
        if (!response.body || typeof response.body.getReader !== 'function') {
          apply({ type: 'error', error: { code: 'no_stream', message: 'Streaming is not supported in this browser.', retryable: false } });
          finish(localState);
          return localState;
        }

        await readSSEStream(response, (event) => {
          if (!event || typeof event !== 'object') return;
          const type = String(event.type || '');
          if (!EVENT_TYPES.has(type)) return;
          if (type === 'meta' && event.stream_id) streamIdSeen = event.stream_id;
          if (type === 'error') {
            terminal = true;
            apply({ type: 'error', error: { message: event.message, code: event.code, retryable: event.retryable !== false } });
          } else {
            if (type === 'done') terminal = true;
            apply({ type, event });
          }
          if (onEventRef.current) onEventRef.current(event, localState);
        });

        if (!terminal) {
          // Stream closed without done/error: keep partial content, mark error.
          apply({ type: 'error', error: { code: 'incomplete', message: 'The connection closed before the answer finished.', retryable: true } });
        }
        finish(localState);
        return localState;
      } catch (err) {
        if (err && (err.name === 'AbortError' || err.code === 'ABORT_ERR')) {
          apply({ type: 'cancelled' });
          if (streamIdSeen) void cancelStream(streamIdSeen);
          finish(localState);
          return localState;
        }
        apply({
          type: 'error',
          error: { code: 'network', message: 'Network error while receiving the answer.', retryable: true },
        });
        finish(localState);
        return localState;
      }
    },
    [finish]
  );

  const cancel = useCallback(() => {
    const controller = controllerRef.current;
    if (!controller) return;
    try {
      controller.abort();
    } catch {
      // ignore
    }
  }, []);

  const retry = useCallback(() => {
    if (!lastRequestRef.current) return null;
    return send(lastRequestRef.current);
  }, [send]);

  const reset = useCallback(() => dispatch({ type: 'reset' }), []);

  return {
    state,
    send,
    cancel,
    retry,
    reset,
    lastRequest: lastRequestRef.current,
    isStreaming: state.isStreaming,
  };
}

export default useCopilotStream;
